import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { AlertCircle, ArrowDown, ArrowUp, Ban, ChevronsUpDown, ExternalLink, RefreshCw, Search, UserRound, X } from "lucide-react";
import { ADMIN_PAGE_SIZE, getAdminUsers, updateUserBan } from "../lib/api";
import { startGoogleSession } from "../lib/auth";
import { useTimeZone } from "../lib/TimeZoneContext";
import { formatTimestamp } from "../lib/timeZone";
import { DEFAULT_USER_SORT, formatPersonName, nextUserSort, sortUsers, type UserSort, type UserSortKey } from "../lib/directory";
import { kycStage, kycStageInfo } from "../lib/kycOps";
import { PageHeading } from "./PageHeading";
import { SkeletonRows } from "./Skeleton";
import { TableEmpty } from "./TableEmpty";
import { FormRequirements } from "./FormRequirements";
import { CopyValue } from "./CopyValue";
import { StageGuidance } from "./StageGuidance";
import { PaginationControls } from "./PaginationControls";
import type { AdminUserSummary, Viewer } from "../types";
import { DEPLOYMENT_LABEL, DEPLOYMENT_NAME } from "../lib/runtime";

const COLUMNS: Array<{ key: UserSortKey; label: string }> = [
  { key: "name", label: "User" },
  { key: "state", label: "Verification" },
  { key: "access", label: "Access" },
  { key: "customerGuid", label: "Customer ID" },
  { key: "createdAt", label: "Created" }
];

export function UsersPage({ viewer, onOpenCustomer }: { viewer: Viewer; onOpenCustomer: (user: AdminUserSummary) => void }) {
  const { timeZone } = useTimeZone();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [selected, setSelected] = useState<AdminUserSummary | null>(null);
  const [sort, setSort] = useState<UserSort>(DEFAULT_USER_SORT);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // A new search invalidates the visible rows, so they clear and the skeleton shows.
    // Refresh goes through loadUsers instead and deliberately keeps them on screen.
    setUsers([]);
    void getAdminUsers({ search: submittedQuery || undefined, skip: (page - 1) * ADMIN_PAGE_SIZE, limit: ADMIN_PAGE_SIZE })
      .then(({ users: result, hasNext: next }) => {
        if (!cancelled) {
          setUsers(result);
          setHasNext(next);
          setSelected((current) => result.find((user) => user.id === current?.id) ?? null);
        }
      })
      .catch((reason) => !cancelled && setError(reason instanceof Error ? reason.message : "Unable to load users"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [page, submittedQuery]);

  const visible = useMemo(() => sortUsers(users, sort), [users, sort]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setSubmittedQuery(query.trim());
  };
  const applySort = (key: UserSortKey) => setSort((current) => nextUserSort(current, key));

  async function loadUsers() {
    setLoading(true);
    try {
      const result = await getAdminUsers({ search: submittedQuery || undefined, skip: (page - 1) * ADMIN_PAGE_SIZE, limit: ADMIN_PAGE_SIZE });
      setUsers(result.users);
      setHasNext(result.hasNext);
      setSelected((current) => result.users.find((user) => user.id === current?.id) ?? null);
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : "Unable to load users");
    } finally {
      setLoading(false);
    }
  }

  const openBan = () => { setReason(""); setConfirmation(""); setError(null); dialogRef.current?.showModal(); };
  const saveBan = async () => {
    if (!selected?.firebaseUid) return;
    setSaving(true); setError(null);
    try {
      await startGoogleSession();
      await updateUserBan({ firebaseUid: selected.firebaseUid, banned: !selected.banned, reason: reason.trim(), confirmation });
      dialogRef.current?.close();
      await loadUsers();
    } catch (saveError) { setError(saveError instanceof Error ? saveError.message : "User access could not be changed"); }
    finally { setSaving(false); }
  };
  const canBan = viewer.role === "superAdmin";
  const expectedConfirmation = selected?.banned ? "UNBAN" : "BAN";

  return (
    <div className="page-stack">
      <PageHeading
        eyebrow="CUSTOMERS"
        title="Users"
        description={`Search the ${DEPLOYMENT_NAME} customer directory and review identity and account access.`}
        info={{
          label: "verification versus access",
          children: <><strong>Two independent things.</strong> <em>Verification</em> is the identity provider's answer about whether this person proved who they are — we can read it and prompt a retry, but not change it. <em>Access</em> is ours: whether we have banned the account from signing in. A verified customer can be banned, and an unverified one can have full access. Emails remain masked by the gateway.</>
        }}
        actions={<>
          <button className="secondary-button" onClick={() => void loadUsers()} disabled={loading}><RefreshCw size={16} className={loading ? "spin" : undefined} aria-hidden="true" />Refresh</button>
          <span className="dev-pill">{DEPLOYMENT_LABEL} DATA</span>
        </>}
      />

      <form className="search-bar" onSubmit={submit}>
        <Search size={18} aria-hidden="true" />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, email, customer GUID, or Firebase UID" aria-label="Search users" />
        <button className="primary-button" type="submit" disabled={loading}>Search</button>
      </form>

      {error ? <div className="error-panel"><AlertCircle size={19} /><div><strong>Users unavailable</strong><p>{error}</p></div></div> : null}

      <div className="explorer-layout wide-detail">
        <section className="history-card explorer-table-card">
          <div className="section-heading-row"><h2>{submittedQuery ? "Search results" : "Recent users"}</h2><span className="history-count">{loading && visible.length === 0 ? "Loading…" : `${visible.length} shown`}</span></div>
          <div className="table-wrap">
            <table aria-busy={loading}>
              <thead>
                <tr>
                  {COLUMNS.map(({ key, label }) => {
                    const active = sort.key === key;
                    return <th key={key} aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
                      <button type="button" className={active ? "sort-header active" : "sort-header"} onClick={() => applySort(key)}>
                        {label}
                        {active
                          ? sort.direction === "asc" ? <ArrowUp size={12} aria-hidden="true" /> : <ArrowDown size={12} aria-hidden="true" />
                          : <ChevronsUpDown size={12} aria-hidden="true" />}
                      </button>
                    </th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {loading && visible.length === 0 ? <SkeletonRows columns={COLUMNS.length} /> : null}
                {!loading && visible.length === 0 ? <TableEmpty columns={COLUMNS.length} filtered={Boolean(submittedQuery)} emptyMessage="No users in this directory yet" onClear={() => { setQuery(""); setSubmittedQuery(""); }} /> : null}
                {visible.map((user) => {
                  const info = kycStageInfo(kycStage(user.state));
                  return <tr key={user.id} className={selected?.id === user.id ? "selected-row" : undefined} onClick={() => setSelected(user)}>
                    <td><strong className="cell-title">{formatPersonName(user.name) ?? "Unnamed user"}</strong><span className="cell-subtitle">{user.phoneNumber || "No phone"}</span></td>
                    {/* The stage carries the meaning; the raw provider string stays
                        underneath it because it is what staff paste into the console. */}
                    <td><span className={`status-badge stage-${info.tone}`}>{info.label}</span><span className="cell-subtitle">{user.state}</span></td>
                    <td><span className={`status-badge ${user.banned ? "status-rejected" : "status-verified"}`}>{user.banned ? "Banned" : "Active"}</span></td>
                    <td className="mono-cell">{user.customerGuid || "—"}</td>
                    <td>{formatTimestamp(user.createdAt, timeZone)}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={ADMIN_PAGE_SIZE} count={visible.length} hasNext={hasNext} loading={loading} onPrevious={() => setPage((value) => Math.max(1, value - 1))} onNext={() => setPage((value) => value + 1)} />
        </section>

        <aside className="detail-card">
          {selected ? <div className="detail-panel">
            <p className="section-label">USER DETAIL</p>
            <h2>{formatPersonName(selected.name) ?? "Unnamed user"}</h2>
            <p className="detail-panel-sub">{selected.phoneNumber || "No phone"}</p>

            <section className="detail-panel-section">
              <div className="detail-panel-heading"><h3>Verification · provider owned</h3></div>
              <p className="detail-panel-note">Cybrid and Persona decide this. The portal can re-read it and prompt the customer to retry, but cannot approve or overturn it.</p>
              <StageGuidance state={selected.state} />
              <dl className="detail-list">
                <div><dt>Raw provider state</dt><dd>{selected.state || "unknown"}</dd></div>
              </dl>
            </section>

            <section className="detail-panel-section">
              <div className="detail-panel-heading"><h3>Access · portal owned</h3></div>
              <p className="detail-panel-note">Whether this account can sign in. Independent of verification — banning does not fail a verification, and a decline does not lock the account.</p>
              <dl className="detail-list">
                <div><dt>Sign-in access</dt><dd><span className={`status-badge ${selected.banned ? "status-rejected" : "status-verified"}`}>{selected.banned ? "Banned" : "Active"}</span></dd></div>
                <div><dt>Last updated</dt><dd>{formatTimestamp(selected.updatedAt, timeZone)}</dd></div>
              </dl>
              {canBan && selected.firebaseUid
                ? <button className={selected.banned ? "secondary-button full-button" : "danger-button full-button"} onClick={openBan}><Ban size={16} />{selected.banned ? "Restore access" : "Ban this account"}</button>
                : null}
            </section>

            <section className="detail-panel-section">
              <div className="detail-panel-heading"><h3>Identifiers</h3></div>
              <dl className="detail-list">
                <div><dt>Customer GUID</dt><dd><CopyValue value={selected.customerGuid} label="customer GUID" /></dd></div>
                <div><dt>Firebase UID</dt><dd><CopyValue value={selected.firebaseUid} label="Firebase UID" /></dd></div>
                <div><dt>Account created</dt><dd>{formatTimestamp(selected.createdAt, timeZone)}</dd></div>
              </dl>
            </section>

            <div className="detail-panel-actions">
              <button className="primary-button full-button" onClick={() => onOpenCustomer(selected)} disabled={!selected.customerGuid}>
                <ExternalLink size={16} />Open full profile
              </button>
              <p className="detail-panel-note">Identity records, KYC case, and this customer’s transaction history on one page.</p>
            </div>
          </div> : <div className="detail-empty"><UserRound size={28} /><strong>Select a user</strong><span>Verification, access, and identifiers appear here.</span></div>}
        </aside>
      </div>
      <dialog className="rate-dialog" ref={dialogRef} onCancel={(event) => { event.preventDefault(); if (!saving) dialogRef.current?.close(); }}>
        <div className="dialog-header"><div><p className="eyebrow">ACCOUNT ACCESS</p><h2>{selected?.banned ? "Restore access" : "Ban this account"}</h2></div><button className="icon-button" onClick={() => dialogRef.current?.close()} disabled={saving} aria-label="Close"><X size={20} /></button></div>
        <div className="warning-copy"><Ban size={19} /><p>{selected?.banned ? `This enables Firebase Auth and removes the ${DEPLOYMENT_NAME} app ban marker. It does not change the customer’s verification state.` : `This disables Firebase Auth, revokes refresh tokens, and creates the ${DEPLOYMENT_NAME} app ban marker. It does not change the customer’s verification state.`}</p></div>
        <label className="field-label">Reason <span>Required</span><textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        <label className="field-label confirmation-field">Type {expectedConfirmation}<input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
        <div className="dialog-actions"><FormRequirements requirements={[{ met: reason.trim().length >= 3, label: "a reason of at least 3 characters" }, { met: confirmation === expectedConfirmation, label: `the phrase ${expectedConfirmation}` }]} /><button className="secondary-button" onClick={() => dialogRef.current?.close()} disabled={saving}>Cancel</button><button className={selected?.banned ? "primary-button" : "danger-button"} disabled={reason.trim().length < 3 || confirmation !== expectedConfirmation || saving} onClick={() => void saveBan()}>{saving ? "Updating…" : "Authenticate & apply"}</button></div>
      </dialog>
    </div>
  );
}
